import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Users, TrendingUp, TrendingDown, DollarSign, BarChart3, Search, Filter, Download,
  ExternalLink, AlertTriangle, CheckCircle, ArrowUpRight, ArrowDownRight, Minus
} from "lucide-react";
import API from "@/lib/api";

function KpiCard({ icon: Icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
  };
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-mono font-bold text-white mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${colors[color]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function VarianceBadge({ value, pct }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-600">No actual</span>;
  const isOver = value > 0;
  const isUnder = value < 0;
  return (
    <div className="flex items-center gap-1">
      {isOver ? <ArrowUpRight className="w-3.5 h-3.5 text-rose-400" /> : isUnder ? <ArrowDownRight className="w-3.5 h-3.5 text-emerald-400" /> : <Minus className="w-3.5 h-3.5 text-slate-500" />}
      <span className={`text-xs font-mono ${isOver ? "text-rose-400" : isUnder ? "text-emerald-400" : "text-slate-400"}`}>
        {isOver ? "+" : ""}{pct?.toFixed(1)}%
      </span>
    </div>
  );
}

export default function ContractorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterName, setFilterName] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("cost-desc");

  useEffect(() => {
    API.get("/analytics/contractor-dashboard")
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load contractor data"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let items = [...data.contractors];
    if (search) {
      const s = search.toLowerCase();
      items = items.filter(c => c.name?.toLowerCase().includes(s) || c.project_name?.toLowerCase().includes(s) || c.client_name?.toLowerCase().includes(s));
    }
    if (filterName !== "all") items = items.filter(c => c.name === filterName);
    if (filterStatus === "tracked") items = items.filter(c => c.has_actual);
    if (filterStatus === "untracked") items = items.filter(c => !c.has_actual);
    // Sort
    switch (sortBy) {
      case "cost-desc": items.sort((a, b) => (b.cost || 0) - (a.cost || 0)); break;
      case "cost-asc": items.sort((a, b) => (a.cost || 0) - (b.cost || 0)); break;
      case "variance": items.sort((a, b) => Math.abs(b.variance || 0) - Math.abs(a.variance || 0)); break;
      case "name": items.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      default: break;
    }
    return items;
  }, [data, search, filterName, filterStatus, sortBy]);

  // Aggregated by name for summary chart
  const byName = useMemo(() => {
    if (!data) return [];
    const map = {};
    data.contractors.forEach(c => {
      const n = c.name || "Unknown";
      if (!map[n]) map[n] = { name: n, projects: 0, totalQuoted: 0, totalActual: 0, tracked: 0 };
      map[n].projects++;
      map[n].totalQuoted += c.cost || 0;
      if (c.has_actual) { map[n].totalActual += c.actual_cost || 0; map[n].tracked++; }
    });
    return Object.values(map).sort((a, b) => b.totalQuoted - a.totalQuoted);
  }, [data]);

  const exportExcel = async () => {
    try {
      const r = await API.get("/analytics/export/benchmarking-excel", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mep_benchmarking_export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center min-h-[50vh]">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="contractor-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="contractor-dashboard-title">Contractor Performance</h1>
          <p className="text-sm text-slate-500 mt-1">Track and compare contractor costs across all projects</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportExcel} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="export-excel-btn">
          <Download className="w-4 h-4 mr-2" />Export Excel
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={Users} label="Contractors" value={s.unique_contractors || 0} sub={`${s.total_engagements || 0} engagements`} color="blue" />
        <KpiCard icon={DollarSign} label="Total Quoted" value={`${(s.total_quoted || 0).toLocaleString("en", { maximumFractionDigits: 0 })}`} color="purple" />
        <KpiCard icon={DollarSign} label="Total Actual" value={`${(s.total_actual || 0).toLocaleString("en", { maximumFractionDigits: 0 })}`} sub={`${s.tracked_count || 0} tracked`} color="green" />
        <KpiCard icon={s.overall_variance > 0 ? TrendingUp : TrendingDown} label="Variance" value={`${s.overall_variance > 0 ? "+" : ""}${(s.overall_variance || 0).toLocaleString("en", { maximumFractionDigits: 0 })}`} color={s.overall_variance > 0 ? "rose" : "green"} />
        <KpiCard icon={BarChart3} label="Tracked" value={`${s.tracked_count || 0}/${s.total_engagements || 0}`} sub="with actuals" color="amber" />
      </div>

      {/* Contractor Summary Bars */}
      {byName.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="border-b border-slate-800 py-3 px-4">
            <CardTitle className="text-sm font-semibold">Spend by Contractor</CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-2">
            {byName.slice(0, 10).map(c => {
              const maxVal = byName[0]?.totalQuoted || 1;
              const quotedW = Math.max(2, (c.totalQuoted / maxVal) * 100);
              const actualW = c.totalActual > 0 ? Math.max(2, (c.totalActual / maxVal) * 100) : 0;
              return (
                <div key={c.name} className="flex items-center gap-3" data-testid={`bar-${c.name}`}>
                  <span className="text-xs text-slate-400 w-32 truncate shrink-0">{c.name}</span>
                  <div className="flex-1 space-y-0.5">
                    <div className="h-3 rounded-sm bg-blue-500/30" style={{ width: `${quotedW}%` }}>
                      <div className="h-full rounded-sm bg-blue-500" style={{ width: "100%" }} />
                    </div>
                    {actualW > 0 && (
                      <div className="h-3 rounded-sm bg-emerald-500/30" style={{ width: `${actualW}%` }}>
                        <div className="h-full rounded-sm bg-emerald-500" style={{ width: "100%" }} />
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-mono text-slate-400 w-20 text-right">{c.totalQuoted.toLocaleString("en", { maximumFractionDigits: 0 })}</span>
                  <span className="text-xs text-slate-600 w-16 text-right">{c.projects} proj</span>
                </div>
              );
            })}
            <div className="flex gap-4 text-[10px] text-slate-600 mt-2">
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-blue-500 inline-block" />Quoted</span>
              <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-emerald-500 inline-block" />Actual</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
          <Input
            placeholder="Search contractor, project, or client..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-slate-900 border-slate-800 text-sm h-9"
            data-testid="contractor-search"
          />
        </div>
        <Select value={filterName} onValueChange={setFilterName}>
          <SelectTrigger className="w-44 bg-slate-900 border-slate-800 h-9 text-sm" data-testid="filter-contractor">
            <Filter className="w-3 h-3 mr-1 text-slate-500" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Contractors</SelectItem>
            {(data?.unique_names || []).map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 bg-slate-900 border-slate-800 h-9 text-sm" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="tracked">With Actuals</SelectItem>
            <SelectItem value="untracked">No Actuals</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36 bg-slate-900 border-slate-800 h-9 text-sm" data-testid="sort-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cost-desc">Cost (High)</SelectItem>
            <SelectItem value="cost-asc">Cost (Low)</SelectItem>
            <SelectItem value="variance">Variance</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No contractor data found</p>
              <p className="text-xs text-slate-600 mt-1">Add contractors to projects to see them here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-500 text-xs">Contractor</TableHead>
                    <TableHead className="text-slate-500 text-xs">Project</TableHead>
                    <TableHead className="text-slate-500 text-xs">Client</TableHead>
                    <TableHead className="text-slate-500 text-xs text-right">Quoted</TableHead>
                    <TableHead className="text-slate-500 text-xs text-right">Actual</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">Variance</TableHead>
                    <TableHead className="text-slate-500 text-xs text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c, i) => (
                    <TableRow key={c.id || i} className="border-slate-800/50 hover:bg-slate-800/30" data-testid={`contractor-row-${i}`}>
                      <TableCell className="font-medium text-sm text-slate-200">{c.name}</TableCell>
                      <TableCell>
                        <Link to={`/projects/${c.project_id}`} className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1">
                          {c.project_name} <ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-slate-400">{c.client_name}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-slate-300">{(c.cost || 0).toLocaleString("en", { maximumFractionDigits: 0 })}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-slate-300">
                        {c.has_actual ? (c.actual_cost || 0).toLocaleString("en", { maximumFractionDigits: 0 }) : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-center"><VarianceBadge value={c.variance} pct={c.variance_pct} /></TableCell>
                      <TableCell className="text-center">
                        {c.has_actual ? (
                          <Badge variant="outline" className="text-[10px] border-emerald-800 text-emerald-400 bg-emerald-500/10">Tracked</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-slate-600 text-center">
        Showing {filtered.length} of {data?.contractors?.length || 0} contractor engagements
      </p>
    </div>
  );
}
